import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 11/02 (Josh): "instead of a label, I just select the features I want them to have … give me the title to the
// feature and then options, read only & read and write … children of the parent". The tree, presets and scopes
// come from GET /users/features/tree (backend/utils/features.py is the one source).
const SCOPE_LABELS = {
  all: 'All deals (staff)',
  broker: "Only deals where their contact is the broker / ISO",
  syndicator: 'Only deals where their contact holds a syndication position',
  funder: 'Only deals where their contact is the funder',
  merchant: 'Only deals where their contact is the merchant',
};
// The old role the preset stands for — kept on the login so role-driven screens keep working.
export const PRESET_ROLES = {
  admin: ['admin'], underwriter: ['underwriter'], investor: ['syndicator', 'investor'],
  broker: ['broker'], funder: ['funder'], merchant: ['merchant'],
};

const Seg = ({ value, onChange, kind, disabled }) => {
  const options = kind === 'rw'
    ? [['off', 'Off'], ['ro', 'Read only'], ['rw', 'Read & write']]
    : [['off', 'Off'], ['on', 'On']];
  return (
    <div className="inline-flex rounded border overflow-hidden text-xs" role="radiogroup">
      {options.map(([v, label]) => (
        <button key={v} type="button" disabled={disabled} role="radio" aria-checked={value === v}
                onClick={() => onChange(v)}
                className={`px-2 py-1 ${value === v ? (v === 'off' ? 'bg-slate-200 text-slate-800' : v === 'ro' ? 'bg-amber-100 text-amber-900' : 'bg-emerald-100 text-emerald-900') : 'bg-white text-slate-500 hover:bg-slate-50'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
          {label}
        </button>
      ))}
    </div>
  );
};

/**
 * Props: features {key: level}, scope string|'' , onChange({features, scope, roles?}), canGrantSuper
 * A preset click fills every box and also returns the matching legacy roles.
 */
export default function FeatureAccessEditor({ features, scope, onChange }) {
  const { getAuthHeaders } = useAuth();
  const [tree, setTree] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    axios.get(`${API}/users/features/tree`, { headers: getAuthHeaders() })
      .then(r => setTree(r.data))
      .catch(() => setError('Could not load the feature list'));
  }, [getAuthHeaders]);

  const set = (key, level) => {
    const next = { ...(features || {}) };
    if (level === 'off') delete next[key]; else next[key] = level;
    onChange({ features: next, scope });
  };
  const applyPreset = (p) => onChange({ features: { ...p.features }, scope: p.scope, roles: PRESET_ROLES[p.key] || [] });
  const clearAll = () => onChange({ features: {}, scope });

  if (error) return <p className="text-xs text-red-600">{error}</p>;
  if (!tree) return <p className="text-xs text-muted-foreground">Loading features…</p>;
  const count = Object.keys(features || {}).length;

  return (
    <div className="space-y-4" data-testid="feature-access-editor">
      <div>
        <Label>Data scope (which deals they see on every page)</Label>
        <div className="mt-1 space-y-1">
          {tree.scopes.map(s => (
            <label key={s} className="flex items-center gap-2 text-sm">
              <input type="radio" name="data_scope" checked={(scope || 'all') === s} onChange={() => onChange({ features: features || {}, scope: s })} />
              <span>{SCOPE_LABELS[s] || s}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">Presets:</span>
        {tree.presets.map(p => (
          <Button key={p.key} type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => applyPreset(p)} data-testid={`preset-${p.key}`}>{p.title}</Button>
        ))}
        <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={clearAll}>Clear all</Button>
        <span className="text-xs text-muted-foreground ml-auto">{count} feature{count === 1 ? '' : 's'} on</span>
      </div>

      <div className="space-y-3">
        {tree.tree.map(group => (
          <div key={group.key} className="rounded border">
            <div className="px-3 py-1.5 bg-slate-50 border-b text-xs font-semibold uppercase tracking-wide text-slate-600">{group.title}</div>
            <div className="divide-y">
              {group.features.map(f => (
                <div key={f.key} className="flex items-start justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{f.title}{f.super_only && <span className="ml-2 text-[10px] uppercase text-rose-600">super admin only</span>}</div>
                    {f.help && <div className="text-xs text-muted-foreground">{f.help}</div>}
                  </div>
                  <Seg kind={f.kind} value={(features || {})[f.key] || 'off'} onChange={v => set(f.key, v)} disabled={!!f.super_only} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Super-admin-only features are granted by the Super admin box above, never here. A login with no feature on falls back to what its roles allowed before this screen existed.</p>
    </div>
  );
}
